/**
 * Error Capture Service
 *
 * Global error listener singleton that captures JavaScript errors
 * for inclusion in bug reports.
 */

export interface CapturedError {
  timestamp: string;
  message: string;
  stack?: string;
  url: string;
  type: 'error' | 'unhandledrejection';
  filename?: string;
  lineno?: number;
  colno?: number;
}

const MAX_ERRORS = 10;
let capturedErrors: CapturedError[] = [];
let isInitialized = false;

/**
 * Initialize error capture listeners
 * Should be called once at app startup
 */
export function initErrorCapture(): void {
  if (isInitialized) {
    return;
  }

  // Capture regular JavaScript errors
  window.addEventListener('error', (event: ErrorEvent) => {
    addError({
      timestamp: new Date().toISOString(),
      message: event.message || 'Unknown error',
      stack: event.error?.stack,
      url: window.location.href,
      type: 'error',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  // Capture unhandled promise rejections
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    let message = 'Unhandled promise rejection';
    let stack: string | undefined;

    if (reason instanceof Error) {
      message = reason.message;
      stack = reason.stack;
    } else if (typeof reason === 'string') {
      message = reason;
    } else if (reason && typeof reason === 'object') {
      message = JSON.stringify(reason);
    }

    addError({
      timestamp: new Date().toISOString(),
      message,
      stack,
      url: window.location.href,
      type: 'unhandledrejection',
    });
  });

  isInitialized = true;
  console.log('[ErrorCapture] Initialized - listening for errors');
}

/**
 * Add an error to the capture list
 * Keeps only the most recent MAX_ERRORS entries
 */
function addError(error: CapturedError): void {
  capturedErrors.push(error);

  // Keep only the most recent errors
  if (capturedErrors.length > MAX_ERRORS) {
    capturedErrors = capturedErrors.slice(-MAX_ERRORS);
  }
}

/**
 * Get all captured errors
 */
export function getRecentErrors(): CapturedError[] {
  return [...capturedErrors];
}

/**
 * Clear all captured errors
 */
export function clearErrors(): void {
  capturedErrors = [];
}

/**
 * Manually capture an error (useful for caught exceptions)
 */
export function captureError(error: Error | string, context?: string): void {
  const errorObj = error instanceof Error ? error : new Error(error);

  addError({
    timestamp: new Date().toISOString(),
    message: context ? `${context}: ${errorObj.message}` : errorObj.message,
    stack: errorObj.stack,
    url: window.location.href,
    type: 'error',
  });
}

/**
 * Get error count
 */
export function getErrorCount(): number {
  return capturedErrors.length;
}
